import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Modal, Button, Space, message, Typography } from 'antd';
import { Stage, Layer, Image as KonvaImage, Rect, Transformer, Group } from 'react-konva';
import useImage from 'use-image';
import { ZoomInOutlined, ZoomOutOutlined, UndoOutlined, SaveOutlined, ExpandOutlined } from '@ant-design/icons';
import {getCanvasAutoComposition, normalizeCanvasConfig} from '../utils/canvasConfig.js';

const { Text } = Typography;

const RefImage = ({ url, shapeProps, imageRef, onSelect, onChange, onLoad }) => {
    const [image, status] = useImage(url, 'anonymous'); // 'anonymous' per evitare problemi CORS
    const loadCalledRef = useRef(false);
    const prevUrlRef = useRef(null);

    // Reset loadCalled quando cambia l'URL
    useEffect(() => {
        if (url !== prevUrlRef.current) {
            prevUrlRef.current = url;
            loadCalledRef.current = false;
        }
    }, [url]);

    useEffect(() => {
        // Chiama onLoad solo una volta quando l'immagine è caricata con successo
        if (image && status === 'loaded' && !loadCalledRef.current) {
            loadCalledRef.current = true;
            onLoad?.(image);
        }
    }, [image, status, onLoad]);

    return (
        <KonvaImage
            onClick={onSelect}
            onTap={onSelect}
            ref={imageRef}
            image={image}
            draggable
            {...shapeProps}
            onDragEnd={(e) => {
                onChange({
                    ...shapeProps,
                    x: e.target.x(),
                    y: e.target.y(),
                });
            }}
            onTransformEnd={() => {
                // transformer updates scale and absolute position; rotation is disabled
                const node = imageRef.current;
                const scaleX = node.scaleX();
                const scaleY = node.scaleY();

                // we will reset it back
                node.scaleX(1);
                node.scaleY(1);

                onChange({
                    ...shapeProps,
                    x: node.x(),
                    y: node.y(),
                    // set minimal value
                    width: Math.max(5, node.width() * scaleX),
                    height: Math.max(5, node.height() * scaleY),
                });
            }}
        />
    );
};


export default function RefEditorModal({ open, onClose, imageSrc, canvasConfig = { width: 1080, height: 1080, fill: '#f0f0f0', fillPercentage: 100 }, initialComposition = null, onSave }) {
    const [scale, setScale] = React.useState(1);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const stageRef = useRef(null);
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const transformerRef = useRef(null);
    const [containerSize, setContainerSize] = useState(null);
    const resolvedCanvasConfig = React.useMemo(() => normalizeCanvasConfig(canvasConfig, { width: 1080, height: 1080, fill: '#f0f0f0', transparency: true, fillPercentage: 100 }), [canvasConfig]);

    // Image props
    const [imgProps, setImgProps] = React.useState(null); // Null inizialmente, in attesa del caricamento

    const [selectedId, selectShape] = React.useState(null);

    // Original dimensions for reset
    const [origDims, setOrigDims] = React.useState(null);

    useEffect(() => {
        if (open) {
            // Reset state on open
            setImgProps(null);
            setOrigDims(null);
            selectShape('image');
        }
    }, [open, imageSrc]);

    useEffect(() => {
        if (!transformerRef.current) return;

        if (selectedId === 'image' && imageRef.current && imgProps) {
            transformerRef.current.nodes([imageRef.current]);
        } else {
            transformerRef.current.nodes([]);
        }

        transformerRef.current.getLayer()?.batchDraw();
    }, [selectedId, imgProps]);

    const calculateContainPosition = (imgW, imgH) => {
        return getCanvasAutoComposition(imgW, imgH, resolvedCanvasConfig);
    };

    const handleImageLoad = (imgObj) => {
        const imgW = imgObj.width;
        const imgH = imgObj.height;
        setOrigDims({ width: imgW, height: imgH });

        // Se abbiamo una composizione iniziale salvata, usala (solo al primo caricamento)
        if (!imgProps && initialComposition && initialComposition.width && initialComposition.height) {
            setImgProps({
                x: initialComposition.x ?? 0,
                y: initialComposition.y ?? 0,
                width: initialComposition.width,
                height: initialComposition.height
            });
            return;
        }

        // Se imgProps esiste già (es. dopo reset o ri-render), non sovrascriverlo automaticamente
        // a meno che non sia stato esplicitamente resettato a null.
        if (!imgProps) {
            setImgProps(calculateContainPosition(imgW, imgH));
        }
    };

    const handleReset = () => {
        if (!origDims) return;
        setImgProps(calculateContainPosition(origDims.width, origDims.height));
        setPosition({ x: 0, y: 0 }); // Reset viewport position if draggable stage
    };

    const handleCenter = () => {
        if (!imgProps) return;
        const canvasW = resolvedCanvasConfig.width;
        const canvasH = resolvedCanvasConfig.height;
        setImgProps(prev => ({
            ...prev,
            x: (canvasW - prev.width) / 2,
            y: (canvasH - prev.height) / 2
        }));
    };

    // Misura il container e calcola lo scale per far entrare il canvas
    useEffect(() => {
        if (!open) {
            setContainerSize(null);
            return;
        }
        const measure = () => {
            if (!containerRef.current) return;
            const { clientWidth, clientHeight } = containerRef.current;
            if (!clientWidth || !clientHeight) return;
            setContainerSize({ width: clientWidth, height: clientHeight });

            const scaleW = clientWidth / resolvedCanvasConfig.width;
            const scaleH = clientHeight / resolvedCanvasConfig.height;
            // 0.8 lascia ~10% di margine per lato intorno al canvas
            const newScale = Math.min(scaleW, scaleH, 1) * 0.8;
            setScale(Math.max(0.1, newScale));
        };
        // Piccolo delay per permettere al Modal di renderizzare
        const timer = setTimeout(measure, 60);
        return () => clearTimeout(timer);
    }, [open, resolvedCanvasConfig]);

    // Dimensioni Stage = intero container; canvas centrato al suo interno
    const stageW = containerSize?.width || resolvedCanvasConfig.width;
    const stageH = containerSize?.height || resolvedCanvasConfig.height;
    // Offset in coordinate logiche per centrare il canvas nello Stage
    const canvasOffsetX = Math.max(0, (stageW / scale - resolvedCanvasConfig.width) / 2);
    const canvasOffsetY = Math.max(0, (stageH / scale - resolvedCanvasConfig.height) / 2);


    const checkDeselect = (e) => {
        // deselect when clicked on empty area
        const clickedOnEmpty = e.target === e.target.getStage();
        if (clickedOnEmpty) {
            selectShape(null);
        }
    };

    const handleSave = () => {
        // In future: logic to export canvas ref
        // const uri = stageRef.current.toDataURL();
        onSave?.(imgProps);
        onClose();
    };

    return (
        <Modal
            title="Editor Referenza"
            open={open}
            onCancel={onClose}
            width="90vw"
            style={{ top: 20 }}
            footer={null}
            destroyOnHidden
        >
            <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
                <Space>
                    <Button icon={<ZoomInOutlined />} onClick={() => setScale(s => Math.min(s + 0.1, 3))} />
                    <Button icon={<ZoomOutOutlined />} onClick={() => setScale(s => Math.max(s - 0.1, 0.1))} />
                    <Button icon={<UndoOutlined />} onClick={handleReset}>Reset</Button>
                    <Button icon={<ExpandOutlined />} onClick={handleCenter}>Centra</Button>
                    <Text type="secondary">Canvas: {resolvedCanvasConfig.width}x{resolvedCanvasConfig.height} · Riempimento: {resolvedCanvasConfig.fillPercentage}%</Text>
                </Space>
                <Button type="primary" icon={<SaveOutlined />} onClick={handleSave}>Salva Modifiche</Button>
            </Space>

            <div ref={containerRef} style={{
                width: '100%',
                height: '70vh',
                background: '#333',
                overflow: 'hidden',
                borderRadius: 8
            }}>
                <Stage
                    width={stageW}
                    height={stageH}
                    scaleX={scale}
                    scaleY={scale}
                    onMouseDown={checkDeselect}
                    onTouchStart={checkDeselect}
                    ref={stageRef}
                >
                    <Layer>
                    <Group x={canvasOffsetX} y={canvasOffsetY}>
                        {/* Sfondo Canvas */}
                        {resolvedCanvasConfig.transparency ? (
                            // Renderizza pattern a scacchiera per trasparenza
                            <Group>
                                <Rect
                                    x={0}
                                    y={0}
                                    width={resolvedCanvasConfig.width}
                                    height={resolvedCanvasConfig.height}
                                    fill="#ffffff"
                                />
                                {/* Simulazione scacchiera semplice o uso di un pattern immagine se possibile.
                                    Per ora usiamo un grigio chiaro alternato se vogliamo, ma per semplicità e performance
                                    spesso si lascia bianco o si usa un CSS background sul div container.
                                    Tuttavia, per vedere i margini del canvas, diamogli un bordo o un colore distinto.
                                */}
                                <Rect
                                    x={0}
                                    y={0}
                                    width={resolvedCanvasConfig.width}
                                    height={resolvedCanvasConfig.height}
                                    fill="#cccccc"
                                    opacity={0.2} // Leggero grigio per distinguere dal foglio bianco
                                />
                                {/* Grid lines opzionali per riferimento */}
                            </Group>
                        ) : (
                            <Rect
                                x={0}
                                y={0}
                                width={resolvedCanvasConfig.width}
                                height={resolvedCanvasConfig.height}
                                fill={resolvedCanvasConfig.fill || '#fff'}
                            />
                        )}

                        {/* Bordo area canvas per delimitazione visiva */}
                        <Rect
                            x={0}
                            y={0}
                            width={resolvedCanvasConfig.width}
                            height={resolvedCanvasConfig.height}
                            stroke="#999"
                            strokeWidth={1 / scale} // Bordo sottile costante indipendentemente dallo zoom
                            listening={false}
                        />

                        {/* Immagine clippata all'area canvas; il transformer resta fuori per mantenere visibili gli handler */}
                        <Group
                            clipX={0}
                            clipY={0}
                            clipWidth={resolvedCanvasConfig.width}
                            clipHeight={resolvedCanvasConfig.height}
                        >
                            <RefImage
                                shapeProps={imgProps || { x: 0, y: 0, width: 0, height: 0 }} // Dummy props finché non carica
                                imageRef={imageRef}
                                url={imageSrc}
                                onSelect={() => selectShape('image')}
                                onChange={setImgProps}
                                onLoad={handleImageLoad}
                            />
                        </Group>

                        {selectedId === 'image' && !!imgProps && (
                            <Transformer
                                ref={transformerRef}
                                rotateEnabled={false}
                                boundBoxFunc={(oldBox, newBox) => {
                                    if (newBox.width < 5 || newBox.height < 5) {
                                        return oldBox;
                                    }
                                    return newBox;
                                }}
                            />
                        )}
                    </Group>
                    </Layer>
                </Stage>
            </div>
        </Modal>
    );
}
